import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ProductShowcase } from "@/components/sections/ProductShowcase";
import { products } from "@/data/productsData";
import { BackToTopButton } from "@/components/BackToTopButton";
import { useTranslation } from 'react-i18next';

// 自社開発プロダクト実績ページ
// Anthropic frontend-design SKILL を参考に、AI default 3 パターン
// (#F4F1EA 暖クリーム / 黒+アシッドグリーン / 新聞風) を回避。
// 既存サイトの dark navy basis + PLANNINGJOY 赤アクセント。

const ProductsPage = () => {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, -120]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('products.page_title')}</title>
        <meta
          name="description"
          content={t('products.meta_description')}
        />
      </Helmet>

      <div className="min-h-screen bg-[#0a0e1a] text-white relative overflow-x-hidden">
        {/* 背景: 微細な grain texture + 動的グラデ */}
        <div className="fixed inset-0 -z-20 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a] via-[#0b1226] to-[#0a0e1a]" />
          <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }} />
        </div>

        {/* 上部ナビゲーション */}
        <header className="relative z-20 px-4 md:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">{t('common.back_to_top')}</span>
          </Link>
          <span className="font-mono text-xs tracking-[0.3em] text-white/40 uppercase">
            Products · Portfolio
          </span>
        </header>

        {/* Hero */}
        <motion.section
          ref={heroRef}
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 lg:pt-28 pb-20 max-w-7xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-rose-400/60" />
              <span className="font-mono text-xs text-rose-300/80 tracking-[0.25em] uppercase">
                In-house engineering
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              {t('products.hero_heading')}
              <br />
              <span className="bg-gradient-to-r from-rose-300 via-white to-cyan-200 bg-clip-text text-transparent">
                {t('products.hero_heading2')}
              </span>
            </h1>

            <p className="max-w-2xl text-base md:text-lg text-white/65 leading-relaxed">
              {t('products.hero_body')}
            </p>

            <div className="flex flex-wrap gap-3 text-xs font-mono text-white/50">
              <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                AI-driven development
              </span>
              <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                React / Next.js / TypeScript
              </span>
              <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                Vercel / Cloud Run deployment
              </span>
              <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                Production-running, not POC
              </span>
            </div>
          </motion.div>
        </motion.section>

        {/* スクロール示唆 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="text-center pb-8"
        >
          <span className="font-mono text-xs text-white/30 tracking-[0.3em]">↓ scroll</span>
        </motion.div>

        {/* プロダクト一覧 */}
        <div className="relative z-10">
          {products.map((product, i) => (
            <ProductShowcase key={product.id} product={product} index={i} />
          ))}
        </div>

        {/* リポ非公開ポリシー (フッター直前) */}
        <section className="relative z-10 py-16 md:py-24 px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 0.8 }}
              className="border-l-2 border-rose-400/50 pl-6 md:pl-8 space-y-4"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-rose-300/80 tracking-[0.25em] uppercase">
                  In-house &amp; Client Development
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                {t('products.inhouse_label')}
              </h3>
              <p className="text-white/65 leading-relaxed">
                {t('products.inhouse_p1')}
              </p>
              <p className="text-white/65 leading-relaxed">
                {t('products.inhouse_p2')}
              </p>
              <div className="pt-2">
                <Link to="/ai-support">
                  <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                    {t('products.ai_service_link')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative z-10 py-20 md:py-28 px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="max-w-2xl mx-auto space-y-6"
          >
            <p className="font-mono text-xs text-white/40 tracking-[0.3em] uppercase">
              Let's build yours
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t('products.cta_heading')}
            </h2>
            <p className="text-white/60 leading-relaxed">
              {t('products.cta_body')}
            </p>
            <div className="pt-2 flex flex-wrap gap-3 justify-center">
              <Link to="/">
                <Button className="bg-white text-[#0a0e1a] hover:bg-white/90 px-6">
                  {t('common.back_to_top')}
                </Button>
              </Link>
              <Link to="/ai-support">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 px-6">
                  {t('products.ai_service_link')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </section>

        <BackToTopButton />
      </div>
    </>
  );
};

export default ProductsPage;
